import { useState } from 'react';
import { RENDER_CONFIG } from '../constants/renderConfig';

/**
 * useRandomizer Hook
 *
 * Provides smooth animated randomization of all ASCII settings.
 * Uses requestAnimationFrame with cubic easing for fluid transitions.
 */
// Generate a random hex color
const randomHexColor = () => {
  const hex = Math.floor(Math.random() * 16777215).toString(16);
  return '#' + hex.padStart(6, '0');
};

export const useRandomizer = ({
  settings,
  setDensity,
  setAnimationSpeed,
  setColumns,
  setGamma,
  setContrast,
  setCharacterCount,
  setGifBackgroundColor,
  setGifForegroundColor,
  mode,
  setCurrentPhase,
  characterSet,
  phaseKeys
}) => {
  const [isRandomizing, setIsRandomizing] = useState(false);

  const randomizeSettings = () => {
    setIsRandomizing(true);

    // Generate random target values
    const randomPhase = phaseKeys[Math.floor(Math.random() * phaseKeys.length)];
    const targetDensity = Math.round((Math.random() * 1.2 + 0.3) * 10) / 10;
    const targetSpeed = Math.floor(Math.random() * 7) * 2 + 4;
    const targetColumns = Math.floor(Math.random() * 17) * 10 + 40;
    const targetGamma = Math.round((Math.random() * 1.5 + 0.5) * 10) / 10;
    const targetContrast = Math.round((Math.random() * 1.5 + 0.5) * 10) / 10;
    const targetCharCount = Math.floor(Math.random() * (characterSet.length - 1)) + 2;

    // Smooth animation using requestAnimationFrame
    const duration = RENDER_CONFIG.animationDuration;
    const startTime = performance.now();

    const startDensity = settings.density;
    const startSpeed = settings.animationSpeed;
    const startColumns = settings.columns;
    const startGamma = settings.gamma;
    const startContrast = settings.contrast;
    const startCharCount = settings.characterCount;

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Smooth ease-in-out cubic
      const easeProgress = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      // Calculate intermediate values with proper rounding and clamping
      const calcDensity = startDensity + (targetDensity - startDensity) * easeProgress;
      const calcSpeed = startSpeed + (targetSpeed - startSpeed) * easeProgress;
      const calcColumns = startColumns + (targetColumns - startColumns) * easeProgress;
      const calcGamma = startGamma + (targetGamma - startGamma) * easeProgress;
      const calcContrast = startContrast + (targetContrast - startContrast) * easeProgress;
      const calcCharCount = startCharCount + (targetCharCount - startCharCount) * easeProgress;

      // Round to slider steps and clamp to min/max
      setDensity(Math.max(0.3, Math.min(1.5, Math.round(calcDensity * 10) / 10)));
      setAnimationSpeed(Math.max(4, Math.min(16, Math.round(calcSpeed / 2) * 2)));
      setColumns(Math.max(40, Math.min(200, Math.round(calcColumns / 10) * 10)));
      setGamma(Math.max(0.5, Math.min(2.0, Math.round(calcGamma * 10) / 10)));
      setContrast(Math.max(0.5, Math.min(2.0, Math.round(calcContrast * 10) / 10)));
      setCharacterCount(Math.max(2, Math.min(characterSet.length, Math.round(calcCharCount))));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDensity(targetDensity);
        setAnimationSpeed(targetSpeed);
        setColumns(targetColumns);
        setGamma(targetGamma);
        setContrast(targetContrast);
        setCharacterCount(targetCharCount);
        setIsRandomizing(false);
      }
    };

    requestAnimationFrame(animate);

    // Change phase immediately
    setCurrentPhase(randomPhase);

    // Randomize colors immediately (no animation needed)
    if (setGifBackgroundColor) setGifBackgroundColor(randomHexColor());
    if (setGifForegroundColor) setGifForegroundColor(randomHexColor());
  };

  return {
    isRandomizing,
    randomizeSettings
  };
};
